import React from "react";

import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Checkbox, Tooltip } from "antd";
import { AnimatePresence, motion } from "framer-motion/dist/framer-motion";

import { SPEC_FLAGS_MAP } from "../../../../Common/constants/SpecFlagConstants";
import NoResults from "../../../../Common/NoResults";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import LockedValue from "../../../../grace-period/components/LockedValue";
import DetailedPanelReport from "./DetailedPanelReport";
import DetailedPesticideReport from "./DetailedPesticideReport";
import { isUnitConversionUnavailableForMissingSpecUnit } from "./helper";
import { getOosFollowUp, isOosReviewSelectable } from "./oosReview";
import SetUnitsToDefaultConfirmPopover from "./SetUnitsToDefaultConfirmPopover";
import UpdateSpecsPromptModal from "./UpdateSpecsPromptModal";

import emptyStateIcon from "../../../../../assets/images/EmptyState_V2.png";
import { ReactComponent as ArrowLeftRightFill } from "../../../../../assets/images/product/arrow-left-right-fill.svg";
import { ReactComponent as FileChartLineIcon } from "../../../../../assets/images/product/file-chart-line.svg";

import "./SampleTestResultTable.css";

export default class SampleTestResultTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasSpecs: true,
      testCheckboxVals: [],
      showSetUnitsToDefaultPopover: false,
      truncatedTooltipKey: null,
    };
  }

  componentDidMount() {
    const { tableData, isPickingRetests } = this.props;
    /** Parse the tableData and check if at least one test has specs. Initialize checkboxes to false. */
    const testCheckboxVals = [];
    let hasSpecs = false;
    if (tableData.length > 0) {
      for (let i = 0; i < tableData.length; i++) {
        testCheckboxVals.push(false);
        if (tableData[i].specs !== "-") {
          hasSpecs = true;
          if (!isPickingRetests) {
            break;
          }
        }
      }
    }

    this.setState({
      hasSpecs,
      testCheckboxVals,
    });
  }

  componentDidUpdate(prevProps) {
    const { tableData, isReviewTab, onReviewSelectionChange } = this.props;
    const prevTests = (prevProps.tableData || []).map((item) => item.test).join("|");
    const nextTests = (tableData || []).map((item) => item.test).join("|");
    const tableChanged = prevTests !== nextTests;
    const leftReview = prevProps.isReviewTab && !isReviewTab;

    if (tableChanged || leftReview) {
      this.clearCheckboxes();
      if ((isReviewTab || leftReview) && onReviewSelectionChange) {
        onReviewSelectionChange([]);
      }
    }
  }

  isReviewRowSelectable = (item) => isOosReviewSelectable(item);

  getReviewSelectableIndexes = () => {
    const { tableData, isReviewTab } = this.props;
    if (!isReviewTab || !tableData || !tableData.length) {
      return [];
    }
    return tableData.reduce((indexes, item, index) => {
      if (this.isReviewRowSelectable(item)) {
        indexes.push(index);
      }
      return indexes;
    }, []);
  };

  emitReviewSelection = (testCheckboxVals) => {
    const { tableData, isReviewTab, onReviewSelectionChange } = this.props;
    if (!isReviewTab || !onReviewSelectionChange) {
      return;
    }
    const selectedTests = tableData
      .filter((item, index) => testCheckboxVals[index] && this.isReviewRowSelectable(item))
      .map((item) => item.test);
    onReviewSelectionChange(selectedTests);
  };

  handleReviewSelectAll = () => {
    const { tableData } = this.props;
    const { testCheckboxVals } = this.state;
    const selectableIndexes = this.getReviewSelectableIndexes();
    if (!selectableIndexes.length) {
      return;
    }
    const allSelected = selectableIndexes.every((index) => testCheckboxVals[index]);
    const nextVals = tableData.map((_, index) => (
      selectableIndexes.includes(index) ? !allSelected : false
    ));
    this.setState({ testCheckboxVals: nextVals });
    this.emitReviewSelection(nextVals);
  };

  /**
   * Set all checkboxes to false,
   * clears selection for next time
   * user is picking tests
   */
  clearCheckboxes = () => {
    const testCheckboxVals = [];
    const { tableData } = this.props;
    if (tableData.length > 0) {
      for (let i = 0; i < tableData.length; i++) {
        testCheckboxVals.push(false);
      }
    }
    this.setState({
      testCheckboxVals,
    });
  }

  /**
   * Select all enabled tests (exclude disabled tests like "due" and "preliminary")
   * Also exclude tests for dates that already have "regular" report in report_path
   * Used for partial generate mode to select all tests by default
   */
  selectAllEnabledTests = () => {
    const {
      tableData, disabledTests, testDate, checkBoxOnChange, isPartialGenerateMode, reportPath,
    } = this.props;
    const testCheckboxVals = [];

    // Check if this date has "regular" report in report_path (not other types like "pesticides")
    const dateHasRegularReport = reportPath && testDate && reportPath[testDate]
      && reportPath[testDate].regular
      && Array.isArray(reportPath[testDate].regular)
      && reportPath[testDate].regular.length > 0;

    if (tableData.length > 0) {
      for (let i = 0; i < tableData.length; i++) {
        const item = tableData[i];
        // Check if test is disabled (due or preliminary)
        const isDisabled = disabledTests instanceof Map
          ? disabledTests.has(`${testDate}|${item.test}`)
          : (disabledTests?.has(item.test) || false);

        // Don't select if disabled or if date has "regular" report
        const shouldSelect = !isDisabled && !dateHasRegularReport;
        testCheckboxVals.push(shouldSelect);

        // Call the onChange handler to add/remove from parent state
        if (shouldSelect && isPartialGenerateMode && testDate) {
          checkBoxOnChange(item.test, true, testDate);
        }
      }
    }

    this.setState({
      testCheckboxVals,
    });
  }

  /**
   * Set test as checked in state,
   * add test to list of selected tests.
   * @param {Number} idx index of test in tableData
   */
  handleCheckClicked = (idx) => {
    const {
      tableData, checkBoxOnChange, isPartialGenerateMode, testDate, reportPath, isReviewTab,
    } = this.props;
    const { testCheckboxVals } = this.state;
    const item = tableData[idx];

    if (isReviewTab) {
      if (!this.isReviewRowSelectable(item)) {
        return;
      }
      const nextVals = [...testCheckboxVals];
      nextVals[idx] = !testCheckboxVals[idx];
      this.setState({ testCheckboxVals: nextVals });
      this.emitReviewSelection(nextVals);
      return;
    }

    // Check if this date has "regular" report in report_path - prevent selection if true
    // Only block if "regular" exists, not other types like "pesticides"
    const dateHasRegularReport = reportPath && testDate && reportPath[testDate]
      && reportPath[testDate].regular
      && Array.isArray(reportPath[testDate].regular)
      && reportPath[testDate].regular.length > 0;

    // If date has "regular" report, don't allow selection
    if (dateHasRegularReport && isPartialGenerateMode) {
      return;
    }

    const checked = !testCheckboxVals[idx];
    /** add or remove test from test set */
    // For partial generate mode, pass the date to track test-date combinations
    if (isPartialGenerateMode && testDate) {
      checkBoxOnChange(item.test, checked, testDate);
    } else {
      checkBoxOnChange(item.test, checked);
    }
    /** set checked state */
    testCheckboxVals[idx] = checked;
    this.setState({ testCheckboxVals: [...testCheckboxVals] });
  };

  /**
   * Handle detailed report view toggle
   * @param {boolean} isOpen
   */
  handleDetailToggle = (isOpen, test) => {
    const { pesticideDetailsToggle } = this.props;
    pesticideDetailsToggle(isOpen, test);
  }

  /**
   * Handle detailed pesticide report close button event
   * @param {boolean} hasCountryListChanged checks if country list in detailed report has changed
   */
  handlePesticideDetailClose = (hasCountryListChanged) => {
    const { reportDataRefresh } = this.props;
    if (hasCountryListChanged) {
      reportDataRefresh();
    }
    this.handleDetailToggle(false, null);
  };

  handlePanelDetailClose = () => {
    this.handleDetailToggle(false, null);
  };

  handleSetUnitsToDefaultPopoverChange = (open) => {
    const { canSetUnitsToDefault, isResettingUnitsToDefault } = this.props;
    if (isResettingUnitsToDefault) {
      return;
    }
    if (open && !canSetUnitsToDefault) {
      return;
    }
    this.setState({ showSetUnitsToDefaultPopover: open });
  };

  handleSetUnitsToDefaultCancel = () => {
    const { isResettingUnitsToDefault } = this.props;
    if (isResettingUnitsToDefault) {
      return;
    }
    this.setState({ showSetUnitsToDefaultPopover: false });
  };

  handleSetUnitsToDefaultConfirm = async () => {
    const { onConfirmSetUnitsToDefault, isResettingUnitsToDefault, testDate } = this.props;
    if (!onConfirmSetUnitsToDefault || isResettingUnitsToDefault) {
      return;
    }

    const didReset = await onConfirmSetUnitsToDefault(testDate);
    if (didReset) {
      this.setState({ showSetUnitsToDefaultPopover: false });
    }
  };

  wrapUpdateSpecsPrompt = (item, children) => {
    const {
      updateSpecsPromptOpen,
      updateSpecsPromptTest,
      onUpdateSpecs,
      onUpdateSpecsNotNow,
      onUpdateSpecsDontShowAgain,
      onUpdateSpecsOpenChange,
      isReviewTab,
    } = this.props;
    const isPromptTarget = !isReviewTab
      && Boolean(updateSpecsPromptOpen)
      && updateSpecsPromptTest === item.test;

    if (!isPromptTarget) {
      return children;
    }

    return (
      <UpdateSpecsPromptModal
        open
        testName={item.test}
        onOpenChange={onUpdateSpecsOpenChange}
        onUpdateSpecs={onUpdateSpecs}
        onNotNow={onUpdateSpecsNotNow}
        onDontShowAgain={onUpdateSpecsDontShowAgain}
      >
        {children}
      </UpdateSpecsPromptModal>
    );
  };

  /**
   * Show the full text tooltip only when the hovered text is truncated
   * @param {Object} event mouse enter event of the text element
   * @param {String} tooltipKey identifies the hovered text
   */
  showTruncatedTooltip = (event, tooltipKey) => {
    const { scrollWidth, clientWidth } = event.currentTarget;
    if (scrollWidth > clientWidth) {
      this.setState({ truncatedTooltipKey: tooltipKey });
    }
  };

  hideTruncatedTooltip = () => {
    const { truncatedTooltipKey } = this.state;
    if (truncatedTooltipKey !== null) {
      this.setState({ truncatedTooltipKey: null });
    }
  };

  renderSpecColumn(type, data, style) {
    const { hasSpecs } = this.state;
    if (type === "header" && hasSpecs) {
      return (
        <th style={style} className="TruncateText" title={data}>
          {data}
        </th>
      );
    } if (type === "row" && hasSpecs) {
      const value = data.specs ? data.specs : "-";
      return (
        <td title={value} className="TruncateText">{value}</td>
      );
    }

    return null;
  }

  renderResultsCell = (item, showCheckboxes, hasMessage) => {
    const resultCell = (
      <div
        className="sampleTestResultTableTestColumn"
        title={item.result ? item.result : "-"}
      >
        {item.result ? (
          <>
            {item.detail ? (
              <div className="ResultBox">
                <span
                  className={`${!showCheckboxes ? "SampleTestResultTable__PesticideResult" : ""} TruncateText`}
                  onClick={
                    !showCheckboxes
                      ? () => {
                        this.handleDetailToggle(true, item.test);
                      }
                      : undefined
                  }
                >
                  {item.result}
                </span>
                {hasMessage && (
                  <Tooltip
                    title={item.message}
                    placement="top"
                    overlayClassName="SampleTestResultTable__TestingInProgressTooltip"
                  >
                    <span className="UnderQCReviewText">
                      {item.message}
                    </span>
                  </Tooltip>
                )}
              </div>
            ) : (
              <div className="ResultBox">
                {item.result}
                {hasMessage && (
                  <Tooltip
                    title={item.message}
                    placement="top"
                    overlayClassName="SampleTestResultTable__TestingInProgressTooltip"
                  >
                    <span className="UnderQCReviewText">
                      {item.message}
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </>
        ) : (
          "-"
        )}
      </div>
    );

    return this.wrapUpdateSpecsPrompt(item, resultCell);
  };

  renderInvestigationFollowUp = (followUp, inProcessTextColor) => {
    const tooltipText = (followUp?.tooltip || "").toString().trim();
    const hasTooltip = Boolean(tooltipText);
    const hasMessage = Boolean((followUp?.message || "").toString().trim());

    return (
      <div className="sampleTestResultTableTestColumn SampleTestResultTable__FollowUp">
        <div className="ResultBox" style={{ color: hasMessage ? inProcessTextColor : undefined }}>
          <span className="SampleTestResultTable__Investigation">
            <span
              className="SampleTestResultTable__InvestigationText TruncateText"
              title={followUp.text}
            >
              {followUp.text}
            </span>
            {hasTooltip && (
              <Tooltip
                title={(
                  <div className="SampleTestResultTable__InvestigationTooltipText">
                    {tooltipText}
                  </div>
                )}
                placement="top"
                classNames={{
                  root: "SampleTestResultTable__InvestigationTooltip SampleTestResultTable__InvestigationTooltip--compact",
                }}
                getPopupContainer={() => document.body}
              >
                <span
                  className="SampleTestResultTable__InvestigationIconHit"
                  aria-label="Investigation details"
                  onClick={(event) => event.stopPropagation()}
                >
                  <InfoCircleOutlined className="SampleTestResultTable__InvestigationIcon" aria-hidden />
                </span>
              </Tooltip>
            )}
          </span>
          {hasMessage && (
            <Tooltip
              title={followUp.message}
              placement="top"
              overlayClassName="SampleTestResultTable__TestingInProgressTooltip"
            >
              <span className="UnderQCReviewText">
                {followUp.message}
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  renderDecisionRequiredFollowUpText = (followUp, resultColor, options = {}) => {
    const { canOpenDetail = false, onOpenDetail, tooltipKey } = options;
    const { truncatedTooltipKey } = this.state;
    const title = followUp?.text || "-";
    const text = followUp?.statusLabel ? (
      <span
        className={`SampleTestResultTable__DecisionRequired TruncateText${canOpenDetail ? " SampleTestResultTable__PesticideResult" : ""}`}
        onClick={canOpenDetail ? onOpenDetail : undefined}
        onMouseEnter={(event) => this.showTruncatedTooltip(event, tooltipKey)}
        onMouseLeave={this.hideTruncatedTooltip}
      >
        <span
          className="SampleTestResultTable__DecisionRequiredResult"
          style={{ color: resultColor }}
        >
          {followUp.resultText || "-"}
        </span>
        <span className="SampleTestResultTable__DecisionRequiredLabel">
          {` - ${followUp.statusLabel}`}
        </span>
      </span>
    ) : (
      <span
        className={`${canOpenDetail ? "SampleTestResultTable__PesticideResult" : ""} TruncateText`}
        style={{ color: resultColor }}
        onClick={canOpenDetail ? onOpenDetail : undefined}
        onMouseEnter={(event) => this.showTruncatedTooltip(event, tooltipKey)}
        onMouseLeave={this.hideTruncatedTooltip}
      >
        {title}
      </span>
    );

    return (
      <Tooltip
        title={(
          <div className="SampleTestResultTable__InvestigationTooltipText">
            {title}
          </div>
        )}
        placement="top"
        open={Boolean(tooltipKey) && truncatedTooltipKey === tooltipKey}
        classNames={{
          root: "SampleTestResultTable__InvestigationTooltip SampleTestResultTable__InvestigationTooltip--compact",
        }}
        getPopupContainer={() => document.body}
      >
        {text}
      </Tooltip>
    );
  };

  renderFollowUpCell = (item, showCheckboxes, inProcessTextColor, resultColor) => {
    const followUp = getOosFollowUp(item);
    const canOpenDetail = item.detail && !showCheckboxes;
    const messageText = (followUp.message || item.message || "").toString().trim();
    const hasMessage = Boolean(messageText);

    if (followUp.type === "investigation") {
      return this.renderInvestigationFollowUp(followUp, inProcessTextColor);
    }

    if (followUp.type === "decision_required") {
      return (
        <div className="sampleTestResultTableTestColumn SampleTestResultTable__FollowUp">
          <div className="ResultBox">
            {this.renderDecisionRequiredFollowUpText(followUp, resultColor, {
              canOpenDetail,
              onOpenDetail: () => this.handleDetailToggle(true, item.test),
              tooltipKey: item.test,
            })}
            {hasMessage && (
              <Tooltip
                title={messageText}
                placement="top"
                overlayClassName="SampleTestResultTable__TestingInProgressTooltip"
              >
                <span className="UnderQCReviewText" style={{ color: inProcessTextColor }}>
                  {messageText}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="sampleTestResultTableTestColumn SampleTestResultTable__FollowUp">
        <div className="ResultBox" style={{ color: hasMessage ? inProcessTextColor : resultColor }}>
          <span
            className={`${canOpenDetail ? "SampleTestResultTable__PesticideResult" : ""} TruncateText`}
            style={{ color: resultColor }}
            onClick={canOpenDetail ? () => this.handleDetailToggle(true, item.test) : undefined}
            title={followUp.text}
          >
            {followUp.text}
          </span>
          {hasMessage && (
            <Tooltip
              title={messageText}
              placement="top"
              overlayClassName="SampleTestResultTable__TestingInProgressTooltip"
            >
              <span className="UnderQCReviewText">
                {messageText}
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  renderResultsReviewTabs = () => {
    const {
      showResultsReviewTabs,
      resultsReviewTab,
      resultsCount,
      reviewCount,
      onResultsReviewTabChange,
      isPickingRetests,
      isPartialGenerateMode,
      tabCountBurst,
      holdResultsReviewTabs,
    } = this.props;

    if (!showResultsReviewTabs || isPickingRetests || isPartialGenerateMode) {
      return null;
    }

    const showResultsPlusOne = (tabCountBurst?.results || 0) > 0;
    const plusOneKey = tabCountBurst?.token
      ? `results-plus-one-${tabCountBurst.token}`
      : "results-plus-one";
    // Keep Review (0) visible through +1 after the last review item is released.
    const showReviewTab = reviewCount > 0 || Boolean(holdResultsReviewTabs);

    return (
      <div className="SampleTestResultTable__ReviewTabs" role="tablist">
        <div className="SampleTestResultTable__ResultsTabWrap">
          <Button
            type="text"
            role="tab"
            aria-selected={resultsReviewTab === "results" || !showReviewTab}
            className={`SampleTestResultTable__ReviewTab${resultsReviewTab === "results" || !showReviewTab ? " SampleTestResultTable__ReviewTab--active" : ""}`}
            onClick={() => onResultsReviewTabChange("results")}
          >
            {`Results (${resultsCount})`}
          </Button>
          <AnimatePresence>
            {showResultsPlusOne && (
              <motion.span
                key={plusOneKey}
                className="SampleTestResultTable__PlusOne"
                aria-hidden
                initial={{ y: 4, opacity: 0 }}
                animate={{ y: [4, 0, -10, -18], opacity: [0, 1, 1, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, times: [0, 0.15, 0.55, 1], ease: "easeOut" }}
              >
                +1
              </motion.span>
            )}
          </AnimatePresence>
        </div>
        {showReviewTab && (
          <Button
            type="text"
            role="tab"
            aria-selected={resultsReviewTab === "review"}
            className={`SampleTestResultTable__ReviewTab${resultsReviewTab === "review" ? " SampleTestResultTable__ReviewTab--active" : ""}`}
            onClick={() => onResultsReviewTabChange("review")}
            disabled={reviewCount === 0}
          >
            {`Review (${reviewCount})`}
          </Button>
        )}
      </div>
    );
  };

  renderTotalTestCount = (count) => (
    <span className="normalWeight SampleTestResultTable__TotalTestContainer">
      <span className="SampleTestResultTable__TotalTestTitle">
        Total test
      </span>
      <span className="SampleTestResultTable__TotalTestValue">
        {count}
      </span>
    </span>
  );

  renderTestInfoTrailing = (count) => {
    const reviewTabs = this.renderResultsReviewTabs();
    if (reviewTabs) {
      return reviewTabs;
    }
    return this.renderTotalTestCount(count);
  };

  renderSetUnitsToDefaultAction = () => {
    const {
      showUnitConversion,
      canSetUnitsToDefault,
      isResettingUnitsToDefault,
      lockResults,
    } = this.props;
    const { showSetUnitsToDefaultPopover } = this.state;

    if (!showUnitConversion || lockResults) {
      return null;
    }

    return (
      <SetUnitsToDefaultConfirmPopover
        open={showSetUnitsToDefaultPopover}
        onOpenChange={this.handleSetUnitsToDefaultPopoverChange}
        onConfirm={this.handleSetUnitsToDefaultConfirm}
        onCancel={this.handleSetUnitsToDefaultCancel}
        loading={isResettingUnitsToDefault}
      >
        <Button
          type="text"
          className="SampleTestResultTable__SetUnitsDefault"
          disabled={!canSetUnitsToDefault}
          icon={!isResettingUnitsToDefault ? (
            <ReloadOutlined className="SampleTestResultTable__SetUnitsDefaultIcon" aria-hidden />
          ) : undefined}
        >
          Set units to default
        </Button>
      </SetUnitsToDefaultConfirmPopover>
    );
  };

  render() {
    const {
      tableData, isPickingRetests, disabledTests, reportData,
      showDetailedPesticideReport, pesticideDetailTest, activeDate, testDate, oldestDate,
      isPartialGenerateMode, reportPath, showUnitConversion, canConvertUnits,
      onConvertUnit, isReviewTab, onReleaseResult, lockResults,
    } = this.props;
    const { testCheckboxVals } = this.state;

    const showOosReviewCheckboxes = isReviewTab && !isPartialGenerateMode;
    const showCheckboxes = isPickingRetests || isPartialGenerateMode || showOosReviewCheckboxes;
    const reviewSelectableIndexes = this.getReviewSelectableIndexes();
    const allReviewSelected = reviewSelectableIndexes.length > 0
      && reviewSelectableIndexes.every((index) => testCheckboxVals[index]);
    const headerStyle = {
      backgroundColor: "var(--color-text-specs-blue)",
      color: "var(--color-content-inverse)",
    };

    // Helper function to check if a test is disabled for this specific date
    const isTestDisabled = (test) => {
      if (!disabledTests || !testDate) return false;
      // Check if disabledTests is a Map (date-specific) or Set (legacy global)
      if (disabledTests instanceof Map) {
        return disabledTests.has(`${testDate}|${test}`);
      }
      // Legacy support for Set-based disabledTests
      return disabledTests.has(test);
    };

    // Check if this date has "regular" report in report_path - tests should be disabled for selection
    // Only block if "regular" exists, not other types like "pesticides"
    // Only apply for partial generate (retest eligibility is handled by setTestsToDisable)
    const dateHasRegularReport = isPartialGenerateMode
      && reportPath && testDate && reportPath[testDate]
      && reportPath[testDate].regular
      && Array.isArray(reportPath[testDate].regular)
      && reportPath[testDate].regular.length > 0;

    const activeDetailItem = tableData.find((item) => item.test === pesticideDetailTest);
    const activeDetailType = activeDetailItem?.detailType;

    return (
      <div>
        {!(showDetailedPesticideReport && activeDetailType === "panel") && (
          <div className="SampleTestResultTable__TestInfo">
            {showDetailedPesticideReport && activeDetailType !== "panel" && (
              <div className="SampleTestResultTable__PesticideDetailTitle">
                Detailed pesticide report
              </div>
            )}
            {!showDetailedPesticideReport && (
              <>
                {this.renderSetUnitsToDefaultAction()}
                {this.renderTestInfoTrailing(tableData.length)}
              </>
            )}
          </div>
        )}
        <div className="SampleTestResultTable__ScrollContainer">
          <StyledScrollbar
            disableTrackYWidthCompensation
            disableTracksWidthCompensation
          >
            <AnimatePresence mode="wait">
              {showDetailedPesticideReport && (
                <motion.div
                  key="DetailedReport"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ duration: 0.1 }}
                >
                  {testDate === activeDate && (
                    activeDetailType === "panel" ? (
                      <DetailedPanelReport
                        reportData={reportData}
                        onClose={this.handlePanelDetailClose}
                        activeDate={activeDate}
                        panelDetailTest={pesticideDetailTest}
                        oldestDate={oldestDate}
                      />
                    ) : (
                      <DetailedPesticideReport
                        reportData={reportData}
                        onClose={this.handlePesticideDetailClose}
                        activeDate={activeDate}
                        pesticideDetailTest={pesticideDetailTest}
                        oldestDate={oldestDate}
                      />
                    )
                  )}
                </motion.div>
              )}
              {!showDetailedPesticideReport && tableData.length > 0 && (
                <div key="results-table" className="SampleTestResultTable__Container">
                  <table className="SampleTestResultTable">
                    <thead className="sampleTestResultTableHeader">
                      <tr>
                        <th style={headerStyle}>
                          {showOosReviewCheckboxes ? (
                            <div className="sampleTestResultTableTestColumn">
                              <div className="sampleTestResultTableCheckBoxContainer">
                                {reviewSelectableIndexes.length > 0 && (
                                  <Checkbox
                                    checked={allReviewSelected}
                                    onChange={this.handleReviewSelectAll}
                                  />
                                )}
                              </div>
                              <div className="sampleTestResultTableTestText">Test</div>
                            </div>
                          ) : (
                            "Test"
                          )}
                        </th>
                        {this.renderSpecColumn("header", "Specs", headerStyle)}
                        <th style={headerStyle}>{isReviewTab ? "Results follow-up" : "Results"}</th>
                        {showUnitConversion && (
                          <th style={headerStyle}>Unit</th>
                        )}
                        {isReviewTab && (
                          <th style={headerStyle} className="SampleTestResultTable__ReleaseHeader">Release Results</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((item, i) => {
                        // Disable selection if test is disabled OR if date has "regular" report
                        const canSelectReviewRow = showOosReviewCheckboxes && this.isReviewRowSelectable(item);
                        const enableTestSelection = showOosReviewCheckboxes
                          ? canSelectReviewRow
                          : (showCheckboxes && !isTestDisabled(item.test) && !dateHasRegularReport);
                        const resultColor = item.test_spec_flag === "-" ? item.color.color : item.color;
                        const hasMessage = item.message?.trim() !== "";
                        const inProcessTextColor = SPEC_FLAGS_MAP[0].color;
                        const shouldLockResult = Boolean(lockResults);
                        const isSpecUnitUnavailable = isUnitConversionUnavailableForMissingSpecUnit({
                          currentSpec: item.specs,
                          specUnit: item.specUnit,
                          testCategory: item.test_category,
                        });
                        const isConvertDisabled = !canConvertUnits || isSpecUnitUnavailable || shouldLockResult;
                        let resultTextColor;
                        if (!shouldLockResult) {
                          resultTextColor = hasMessage ? inProcessTextColor : resultColor;
                        }
                        const lockedResultCell = (
                          <div className="sampleTestResultTableTestColumn">
                            <LockedValue locked />
                          </div>
                        );
                        let convertUnavailableTooltip = "";
                        if (isSpecUnitUnavailable) {
                          convertUnavailableTooltip = "Unit conversion is unavailable because the spec does not have a unit.";
                        }
                        const useRowSelectionHover = enableTestSelection && !showOosReviewCheckboxes;
                        const canReleaseResult = this.isReviewRowSelectable(item);
                        return (
                          <tr
                            key={`${item.test}-${i}`}
                            onClick={
                              useRowSelectionHover
                                ? () => this.handleCheckClicked(i)
                                : null
                            }
                            className={useRowSelectionHover ? "tempClass" : undefined}
                            style={{
                              cursor: useRowSelectionHover
                                ? "pointer"
                                : "default",
                            }}
                          >
                            <td
                              className={canSelectReviewRow ? "SampleTestResultTable__SelectCell" : ""}
                              onClick={canSelectReviewRow ? () => this.handleCheckClicked(i) : undefined}
                              style={canSelectReviewRow ? { cursor: "pointer" } : undefined}
                            >
                              <div className="sampleTestResultTableTestColumn">
                                {showCheckboxes && (
                                  <div
                                    className="sampleTestResultTableCheckBoxContainer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Checkbox
                                      disabled={showOosReviewCheckboxes ? !canSelectReviewRow : !enableTestSelection}
                                      checked={Boolean(testCheckboxVals[i])}
                                      onChange={() => this.handleCheckClicked(i)}
                                    />
                                  </div>
                                )}
                                <div
                                  className={`sampleTestResultTableTestText${showCheckboxes && !enableTestSelection ? " sampleTestResultTableTestText--disabled" : ""}`}
                                  title={item.test}
                                >
                                  {item.test}
                                </div>
                              </div>
                            </td>
                            {this.renderSpecColumn("row", item)}
                            {isReviewTab ? (
                              <td>
                                {shouldLockResult
                                  ? lockedResultCell
                                  : this.renderFollowUpCell(item, showCheckboxes, inProcessTextColor, resultColor)}
                              </td>
                            ) : (
                              <td style={{ color: resultTextColor }}>
                                {shouldLockResult
                                  ? lockedResultCell
                                  : this.renderResultsCell(item, showCheckboxes, hasMessage)}
                              </td>
                            )}
                            {showUnitConversion && (
                              <td className="SampleTestResultTable__UnitCell">
                                <div className="SampleTestResultTable__UnitCellInner">
                                  <StyledTooltip
                                    title={convertUnavailableTooltip}
                                    placement="top"
                                  >
                                    <span className="SampleTestResultTable__UnitButtonWrap">
                                      <Button
                                        type="default"
                                        className="SampleTestResultTable__UnitButton"
                                        title={isSpecUnitUnavailable ? undefined : (item.unit || "-")}
                                        disabled={isConvertDisabled}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          if (isConvertDisabled || !onConvertUnit) {
                                            return;
                                          }
                                          onConvertUnit(item, testDate);
                                        }}
                                      >
                                        <span className="SampleTestResultTable__UnitText TruncateText">
                                          {item.unit || "-"}
                                        </span>
                                        <ArrowLeftRightFill className="SampleTestResultTable__UnitIcon" aria-hidden />
                                      </Button>
                                    </span>
                                  </StyledTooltip>
                                </div>
                              </td>
                            )}
                            {isReviewTab && (
                              <td className="SampleTestResultTable__ReleaseCell">
                                <Tooltip
                                  title={canReleaseResult
                                    ? ""
                                    : "Cannot release results while investigation is in process"}
                                  placement="top"
                                  classNames={{ root: "SampleTestResultTable__InvestigationTooltip" }}
                                  getPopupContainer={() => document.body}
                                >
                                  <span className="SampleTestResultTable__ReleaseBtnWrap">
                                    <Button
                                      type="default"
                                      className="SampleTestResultTable__ReleaseBtn"
                                      disabled={!canReleaseResult}
                                      icon={<FileChartLineIcon className="SampleTestResultTable__ReleaseBtnIcon" aria-hidden />}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (!canReleaseResult || !onReleaseResult) {
                                          return;
                                        }
                                        onReleaseResult(item, testDate);
                                      }}
                                    >
                                      Release Result
                                    </Button>
                                  </span>
                                </Tooltip>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {!showDetailedPesticideReport && tableData.length === 0 && (
                <div key="results-empty" className="SampleTestResultTable__NoResults">
                  <NoResults
                    image={emptyStateIcon}
                    message="No Result Found"
                    className="SampleTestResultTable__NoResultsContent"
                  />
                </div>
              )}
            </AnimatePresence>
          </StyledScrollbar>
        </div>
      </div>
    );
  }
}
